// =====================================================
// VISUAL RENDERER — the on-screen redline (full view and change stepper).
//
// Word documents are drawn the way Word lays them out: each paragraph carries its
// resolved style + direct formatting (alignment, indents, spacing, line height, caps,
// highlight, auto-numbering), real whitespace (tabs, leading spaces, line breaks), and
// table rows render as real tables. The formatting comes from doc-layout.ts, attached to
// the parsed paragraphs at parse time; documents without it (PDF, text) fall back to
// each run's direct formatting. Preview only — the .docx output never reads this.
// =====================================================

import type { DiffResult, DiffParagraph, DiffEntry, RichParagraph, RichToken, CellInfo } from '../types';
import { isProActive } from '../bridge';
import { pieceSegments } from './segments';
import { sideOf as pieceSide, diffPieces } from './pieces';
import { computeWordDiff, normalizeQuotes } from './diff-engine-core';
import {
  resolvePara, resolveRun, labelOf, resolveTable, resolveCell, rowHeightTw, rowPosition, pageOf,
  type RunFmt, type ParaFmt, type Border,
} from './doc-layout';
import { emptyTrackedBlank } from './docx-tracking';
import { cellParagraphPlan, rowCellsComparable } from './docx-tables';
import { htmlWithWordmark } from './wordmark';
import { bodyChanged } from './counts';

// Known serif fonts — used to pick the right CSS generic fallback
const SERIF_FONTS = new Set([
  'times new roman', 'times', 'georgia', 'garamond', 'palatino', 'palatino linotype',
  'book antiqua', 'baskerville', 'cambria', 'didot', 'bodoni', 'century schoolbook',
  'goudy old style', 'hoefler text', 'constantia', 'century', 'bookman old style',
]);

// Metric-compatible (or closest available) stand-ins for fonts most Macs/phones lack.
const FONT_STACKS: Record<string, string> = {
  'century schoolbook': "'Century Schoolbook','Fredline Schola','New Century Schoolbook','TeX Gyre Schola','C059',Century,serif",
  'calibri': "Calibri,'Fredline Sans',Carlito,'Helvetica Neue',Arial,sans-serif",
  'cambria': "Cambria,'Fredline Caladea',Caladea,Georgia,serif",
  'times new roman': "'Times New Roman',Times,'Liberation Serif','Tinos',serif",
  'arial': "Arial,Helvetica,'Liberation Sans','Arimo',sans-serif",
  'garamond': "Garamond,'EB Garamond','Adobe Garamond Pro',serif",
  'book antiqua': "'Book Antiqua',Palatino,'Palatino Linotype','TeX Gyre Pagella',serif",
  'bookman old style': "'Bookman Old Style','Bookman','URW Bookman','TeX Gyre Bonum',serif",
  'courier new': "'Courier New',Courier,'Liberation Mono',monospace",
};

// Word's "single" line height as a multiple of the font size (ascent + descent + gap).
const LINE_FACTOR: Record<string, number> = {
  'times new roman': 1.15, 'calibri': 1.22, 'century schoolbook': 1.2, 'cambria': 1.17,
  'arial': 1.15, 'georgia': 1.14, 'garamond': 1.12, 'book antiqua': 1.17, 'courier new': 1.13,
};

function fontStack(name: string): string {
  const key = name.toLowerCase();
  if (FONT_STACKS[key]) return FONT_STACKS[key];
  const safe = name.replace(/'/g, "\\'");
  return `'${safe}',${SERIF_FONTS.has(key) ? 'serif' : 'sans-serif'}`;
}

const tw = (v: number) => `${+(v / 20).toFixed(2)}pt`;

// ---------- run formatting → CSS ----------

type Props = Record<string, string>;

/** CSS for a run's formatting. Decoration/colour are left out on changed text (the diff classes own them). */
function runProps(f: RunFmt, skipDecorations: boolean): Props {
  const p: Props = {};
  if (f.font) p['font-family'] = fontStack(f.font);
  const sub = f.vertAlign === 'superscript' || f.vertAlign === 'subscript';
  if (f.sz) p['font-size'] = `${+((f.sz / 2) * (sub ? 0.65 : 1)).toFixed(2)}pt`;
  else if (sub) p['font-size'] = 'smaller';
  if (sub) p['vertical-align'] = f.vertAlign === 'superscript' ? 'super' : 'sub';
  p['font-weight'] = f.b ? 'bold' : 'normal';
  p['font-style'] = f.i ? 'italic' : 'normal';
  p['text-transform'] = f.caps ? 'uppercase' : 'none';
  p['font-variant'] = !f.caps && f.smallCaps ? 'small-caps' : 'normal';
  const bg = f.highlight && f.highlight !== 'none' ? f.highlight : f.shd;
  if (bg) p['background-color'] = bg;
  if (!skipDecorations) {
    const deco = [f.u ? 'underline' : '', f.strike || f.dstrike ? 'line-through' : ''].filter(Boolean).join(' ');
    if (deco) p['text-decoration'] = deco;
    if (f.color && f.color !== 'auto' && f.color.toLowerCase() !== '#000000') p['color'] = f.color;
  }
  return p;
}

const DEFAULTS: Props = { 'font-weight': 'normal', 'font-style': 'normal', 'text-transform': 'none', 'font-variant': 'normal' };

/** Inline CSS for a paragraph's base formatting (only what differs from the defaults). */
function baseCss(p: Props): string {
  return Object.keys(p).filter(k => DEFAULTS[k] !== p[k]).map(k => `${k}:${p[k]}`).join(';');
}

/** Inline CSS for a run inside a paragraph whose base is `base`: only what differs. */
function deltaCss(base: Props, run: Props): string {
  const out: string[] = [];
  for (const k of Object.keys(run)) if (base[k] !== run[k]) out.push(`${k}:${run[k]}`);
  // A property the paragraph has but the run lacks must be reset.
  for (const k of Object.keys(base)) {
    if (k in run) continue;
    if (k === 'background-color') out.push('background-color:transparent');
    else if (k === 'text-decoration') out.push('text-decoration:none');
    else if (k === 'color') out.push('color:var(--doc-ink,inherit)');
    else if (k === 'vertical-align') out.push('vertical-align:baseline');
  }
  return out.join(';');
}

// ---------- whitespace ----------
// The whitespace around words comes from pieces.ts, the same as the .docx writers use.

// ---------- pieces → DOM ----------

interface Piece { gap: string; text: string; css: string; cls: string; /** its diff entry (entryTags) */ entry?: number }

/** Draw each changed piece with the diff entries it shows (data-e), for the review page to tie
 *  it to its change in the writer's record. Off for every other drawing (renderRedline option). */
let entryTags = false;

/** Append pieces, merging neighbours with the same class and CSS into one span
 *  (so an inserted phrase or a highlight is continuous across its spaces, as in Word). */
function emitPieces(parent: HTMLElement, pieces: Piece[]): void {
  let cur: { text: string; css: string; cls: string; entries: number[] } | null = null;
  const flush = () => {
    if (!cur || !cur.text) { cur = null; return; }
    if (cur.css || cur.cls) {
      const span = document.createElement('span');
      if (cur.cls) span.className = cur.cls;
      if (cur.css) span.style.cssText = cur.css;
      if (cur.cls && cur.entries.length) span.setAttribute('data-e', [...new Set(cur.entries)].join(' '));
      span.textContent = cur.text;
      parent.appendChild(span);
    } else {
      parent.appendChild(document.createTextNode(cur.text));
    }
    cur = null;
  };
  for (const p of pieces) {
    // (a page or column break in the whitespace, \f or \v, is drawn as a line break)
    const gap = p.gap.replace(/[\f\v]/g, '\n');
    if (cur && cur.cls === p.cls && cur.css === p.css) { cur.text += gap + p.text; if (p.entry != null) cur.entries.push(p.entry); continue; }
    flush();
    if (gap) parent.appendChild(document.createTextNode(gap));
    cur = { text: p.text, css: p.css, cls: p.cls, entries: p.entry != null ? [p.entry] : [] };
  }
  flush();
}

/** One side of a paragraph being drawn: its formatting context, tokens and gaps. */
interface Side { rich?: RichParagraph; tokens: RichToken[]; gaps: string[] | null }

function sideOf(rich: RichParagraph | undefined): Side {
  const s = pieceSide(rich);
  return { rich, tokens: s.tokens, gaps: s.gaps };
}

const cssCache = new WeakMap<Props, Map<string, string>>();

function tokenPieces(side: Side, base: Props, token: RichToken | undefined, value: string,
                     skip: boolean, charIdx?: number): { text: string; css: string }[] {
  let cache = cssCache.get(base);
  if (!cache) { cache = new Map(); cssCache.set(base, cache); }
  const cssFor = (rPrXml: string) => {
    const k = (skip ? '1' : '0') + rPrXml;
    let v = cache!.get(k);
    if (v === undefined) { v = deltaCss(base, runProps(resolveRun(side.rich, rPrXml), skip)); cache!.set(k, v); }
    return v;
  };
  const segs = pieceSegments(token, value, charIdx);
  if (segs) return segs.map(s => ({ text: s.text, css: cssFor(s.rPrXml) }));
  return [{ text: value, css: cssFor(token ? token.rPrXml : '') }];
}

// ---------- paragraph box ----------

interface Box { div: HTMLElement; base: Props; skip: boolean }

/**
 * Create the paragraph element with its Word formatting. `rich` supplies the
 * formatting; `skip` drops run decoration/colour (whole-paragraph changes).
 */
function paragraphBox(rich: RichParagraph | undefined, className: string, skip: boolean): Box {
  const div = document.createElement('div');
  div.className = className;
  if (!rich) return { div, base: {}, skip };
  const { para, layout } = resolvePara(rich);
  // Base run formatting: the paragraph's first real word (or its mark when empty),
  // so most text needs no span of its own.
  const letter = rich.richTokens.find(t => /[\p{L}\p{N}]/u.test(t.word)) || rich.richTokens[0];
  const markRPr = (rich.pPrXml || '').match(/<w:rPr\b[\s\S]*?<\/w:rPr>/)?.[0] || '';
  const fmt = resolveRun(rich, letter ? (letter.segments ? letter.segments[0].rPrXml : letter.rPrXml) : markRPr);
  const base = runProps(fmt, skip);
  // Decoration and run colour stay on spans, so a paragraph's first word does not
  // underline or colour the rest of it.
  delete base['text-decoration'];
  delete base['color'];
  delete base['background-color'];
  let css = baseCss(base);
  if (layout) {
    div.classList.add('docx-p');
    css += (css ? ';' : '') + paraCss(para, fmt);
  }
  if (css) div.style.cssText = css;
  return { div, base, skip };
}

function paraCss(p: ParaFmt, fmt: RunFmt): string {
  const out: string[] = [];
  const jc = p.jc;
  const align = jc === 'both' || jc === 'distribute' || jc === 'thaiDistribute' ? 'justify'
    : jc === 'center' ? 'center' : jc === 'right' || jc === 'end' ? 'right' : 'left';
  out.push(`--ta:${align}`);
  // Word squeezes the spaces of a justified line by up to about a fifth to fit one more
  // word; a browser only stretches them. Starting from narrower spaces lets the browser
  // break where Word does, and justification then widens them again.
  if (align === 'justify') out.push('word-spacing:-0.05em');
  if (p.indL) out.push(`--ind-l:${tw(p.indL)}`);
  if (p.indR) out.push(`--ind-r:${tw(p.indR)}`);
  if (p.indFirst) out.push(`--ind-fl:${tw(p.indFirst)}`);
  if (p.spBefore) out.push(`--sp-b:${tw(p.spBefore)}`);
  if (p.spAfter) out.push(`--sp-a:${tw(p.spAfter)}`);
  const factor = LINE_FACTOR[(fmt.font || '').toLowerCase()] || 1.17;
  const sizePt = (fmt.sz || 22) / 2;
  let lh = String(factor);
  if (p.line) {
    if (p.lineRule === 'exact') lh = tw(p.line);
    else if (p.lineRule === 'atLeast') lh = `${+Math.max(p.line / 20, factor * sizePt).toFixed(2)}pt`;
    else lh = String(+(factor * p.line / 240).toFixed(3));
  }
  out.push(`--lh:${lh}`);
  if (p.shd) out.push(`--p-bg:${p.shd}`);
  if (p.bdr) {
    for (const side of ['top', 'left', 'bottom', 'right'] as const) {
      const b = p.bdr[side];
      if (!b || b.style === 'none') continue;
      out.push(`border-${side}:${borderCss(b)}`, `padding-${side}:${b.spacePt}pt`);
    }
  }
  return out.join(';');
}

function borderCss(b: Border | undefined): string {
  return !b || b.style === 'none' ? 'none' : `${b.widthPt}pt ${b.style} ${b.color}`;
}

/** Number label ("1.", "(a)", bullet) plus its tab/space, as the first piece. */
function labelPiece(rich: RichParagraph | undefined, box: Box): Piece | null {
  const label = labelOf(rich);
  if (!label || !(label.text + label.suff)) return null;
  const props = runProps(label.run, box.skip);
  return { gap: '', text: label.text + label.suff, css: deltaCss(box.base, props), cls: '' };
}

/** Draw a whole paragraph's own text (no word diff) into its box. */
function fillPlain(box: Box, rich: RichParagraph | undefined, fallbackText: string): void {
  const pieces: Piece[] = [];
  const lp = labelPiece(rich, box);
  if (lp) pieces.push(lp);
  const side = sideOf(rich);
  if (rich && side.tokens.length) {
    for (let i = 0; i < side.tokens.length; i++) {
      const token = side.tokens[i];
      const gap = side.gaps ? side.gaps[i] : (i > 0 ? ' ' : '');
      tokenPieces(side, box.base, token, token.word, box.skip).forEach((pc, k) =>
        pieces.push({ gap: k === 0 ? gap : '', text: pc.text, css: pc.css, cls: '' }));
    }
  } else if (fallbackText.trim()) {
    pieces.push({ gap: '', text: fallbackText, css: '', cls: '' });
  }
  if (!pieces.length) { box.div.textContent = ' '; return; }
  emitPieces(box.div, pieces);
}

const CLASS_MAP: Record<string, string> = {
  'delete': 'diff-del',
  'insert': 'diff-add',
  'move-from': 'diff-move-from-word',
  'move-to': 'diff-move-to-word',
};

/** Draw a word diff into a box. Equal/inserted words come from `rev`, deleted from `orig`.
 *  `tag`: a body paragraph's own diff (its entries are what the writer's record names). */
function fillDiff(box: Box, diff: DiffEntry[], orig: Side, rev: Side, labelFrom?: RichParagraph, tag = false): void {
  const pieces: Piece[] = [];
  const lp = labelPiece(labelFrom ?? rev.rich, box);
  if (lp) pieces.push(lp);
  const index = tag && entryTags ? new Map<DiffEntry, number>(diff.map((d, k) => [d, k])) : null;
  for (const pc of diffPieces(diff, orig, rev, { visual: true })) {
    const d = pc.entry;
    const isChange = d.type !== 'equal';
    const cls = CLASS_MAP[d.type] || '';
    const entry = index && isChange ? index.get(d) : undefined;
    tokenPieces(pc.fromRev ? rev : orig, box.base, pc.token, d.value, isChange, pc.charIdx).forEach((p, k) =>
      pieces.push({ gap: k === 0 ? pc.gap : '', text: p.text, css: p.css, cls, entry }));
  }
  if (!pieces.length) { box.div.textContent = ' '; return; }
  emitPieces(box.div, pieces);
}

// ---------- full redline ----------

export function renderRedline(
  diffResult: DiffResult,
  redlineBody: HTMLElement,
  statElements?: {
    statDel: HTMLElement;
    statAdd: HTMLElement;
    statMoves: HTMLElement;
    statUnchanged: HTMLElement;
  },
  truncationInfo?: { truncated: boolean; totalEstPages: number },
  docFontInfo?: { defaultFont?: string; defaultFontSize?: number; entryTags?: boolean;
    /** footnotes, endnotes, headers or footers changed (the no-changes notice then says so) */
    outsideBody?: boolean }
): void {
  entryTags = !!docFontInfo?.entryTags;
  try { drawRedline(diffResult, redlineBody, statElements, truncationInfo, docFontInfo); }
  finally { entryTags = false; }
}

function drawRedline(
  diffResult: DiffResult,
  redlineBody: HTMLElement,
  statElements?: { statDel: HTMLElement; statAdd: HTMLElement; statMoves: HTMLElement; statUnchanged: HTMLElement },
  truncationInfo?: { truncated: boolean; totalEstPages: number },
  docFontInfo?: { defaultFont?: string; defaultFontSize?: number; outsideBody?: boolean }
): void {
  const { paragraphs, stats } = diffResult;

  if (statElements) {
    statElements.statDel.textContent = String(stats.deletions);
    statElements.statAdd.textContent = String(stats.additions);
    statElements.statMoves.textContent = String(stats.moves);
    statElements.statUnchanged.textContent = String(stats.unchanged);
  }

  // Clear existing content
  redlineBody.textContent = '';
  redlineBody.classList.remove('has-page');

  // Apply document default font as base style on the container
  if (docFontInfo?.defaultFont) {
    redlineBody.style.fontFamily = fontStack(docFontInfo.defaultFont);
  }
  if (docFontInfo?.defaultFontSize) {
    redlineBody.style.fontSize = `${docFontInfo.defaultFontSize}pt`;
  }

  // A Word document is drawn on a page of its own size and margins.
  let target: HTMLElement = redlineBody;
  let page: ReturnType<typeof pageOf>;
  for (const p of paragraphs) {
    page = pageOf(p.revRich) || pageOf(p.origRich);
    if (page) break;
  }
  if (page) {
    redlineBody.classList.add('has-page');
    target = document.createElement('div');
    target.className = 'doc-page';
    target.style.cssText = `--page-w:${tw(page.widthTw)};--page-ml:${tw(page.marginLTw)};--page-mr:${tw(page.marginRTw)};--tab:${tw(page.tabTw)}`;
    redlineBody.appendChild(target);
  }

  // Show a notice if the body has no change at all (the app rewrites it when footnotes, endnotes,
  // headers or footers changed: ResultsArea)
  if (!bodyChanged(paragraphs)) {
    const notice = document.createElement('div');
    notice.className = 'no-changes-notice';
    notice.style.cssText = 'background:#fff8e1;border:1px solid #ffe082;border-radius:8px;padding:16px 20px;margin:0 0 16px;font-family:var(--font-body);color:#6d4c00;font-size:12px;font-weight:700;white-space:normal;';
    notice.innerHTML = docFontInfo?.outsideBody
      ? '<strong>No changes in the body text.</strong> The changes are in footnotes, endnotes, headers or footers: see “Changes outside the body text” at the end.'
      : '<strong>No changes detected.</strong> The two documents appear to have identical text content. If you expected differences, verify that you uploaded two distinct versions (not the same file twice).';
    target.appendChild(notice);
  }

  // Adaptive chunk size: smaller chunks for very large documents to keep UI responsive
  const CHUNK_SIZE = paragraphs.length > 2000 ? 100 : 200;
  let chunkIdx = 0;
  const tags = entryTags;   // (a later chunk is drawn after renderRedline returned)

  function renderChunk() {
    const was = entryTags;
    entryTags = tags;
    try { drawChunk(); } finally { entryTags = was; }
  }
  function drawChunk() {
    const frag = document.createDocumentFragment();
    const end = Math.min(chunkIdx + CHUNK_SIZE, paragraphs.length);

    for (let p = chunkIdx; p < end; p++) {
      const para = paragraphs[p];

      // Free tier divider — inserted between compared and remainder sections
      if ((para as any)._freeTierDivider) {
        const notice = document.createElement('div');
        notice.className = 'fredline-truncation-notice';
        notice.innerHTML =
          '<strong>Free comparison limit reached.</strong> ' +
          'The first ~5 pages were compared and redlined above. The rest of your document is shown below without change markup. ' +
          '<a href="/#pricing">Get the full version</a> for every page, with no watermark.';
        frag.appendChild(notice);
        continue;
      }

      // id="p-<idx>" lets the change stepper jump to a paragraph in the full view.
      const el = renderParagraph(para, frag);
      if (el) el.id = `p-${p}`;
    }

    target.appendChild(frag);
    chunkIdx = end;

    if (chunkIdx < paragraphs.length) {
      requestAnimationFrame(renderChunk);
    } else {
      // Watermark for free tier
      if (!isProActive()) {
        const watermark = document.createElement('div');
        watermark.className = 'fredline-watermark';
        watermark.innerHTML = htmlWithWordmark('Generated with Fredline — fredline.app');
        target.appendChild(watermark);
      }
    }
  }

  renderChunk();
}

const WHOLE_CLASS: Record<string, string> = {
  'para-delete': 'diff-para-del',
  'para-insert': 'diff-para-add',
  'move-from': 'diff-move-from',
  'move-to': 'diff-move-to',
};

/**
 * Render one diff paragraph into `el` (appended as a `div.diff-paragraph`), exactly as
 * the full redline does. Returns the element, or null for a paragraph that renders as
 * nothing (an empty deleted/inserted/moved paragraph). Shared by renderRedline and the
 * change stepper so both show a change identically. Free-tier dividers are not
 * paragraphs and are handled by the caller.
 */
export function renderParagraph(para: DiffParagraph, el: HTMLElement | DocumentFragment): HTMLElement | null {
  // A paragraph that became a table row (or the reverse), or a changed passthrough block (content
  // control, TOC): the .docx shows it as the old block deleted and the new one inserted, and so
  // does the preview (it used to draw only the new side, hiding the deleted text).
  const mixedRow = !!(para.origRich?.isTableRow || para.revRich?.isTableRow) && !rowCellsComparable(para);
  const passBlock = !!(para.origRich?.isPassthrough || para.revRich?.isPassthrough);
  if (para.type === 'modify' && (mixedRow || passBlock) && para.origRich && para.revRich) {
    const wrap = document.createElement('div');
    wrap.className = 'diff-replaced';
    renderParagraph({ type: 'para-delete', origRich: para.origRich, text: para.origRich.text }, wrap);
    renderParagraph({ type: 'para-insert', revRich: para.revRich, text: para.revRich.text }, wrap);
    el.appendChild(wrap);
    return wrap;
  }
  const fromOrig = para.type === 'para-delete' || para.type === 'move-from';
  const rich = fromOrig ? para.origRich : (para.revRich || para.origRich);
  const hasChanges = para.type === 'modify'
    ? para.diff!.some((d: DiffEntry) => d.type !== 'equal' && d.value.trim())
    : para.type !== 'equal' || !!para.paraMark;

  if (para.type !== 'equal' && para.type !== 'modify' && !(para.text || '').trim()) {
    // a blank line inserted or removed (written as a paragraph-mark revision in the .docx):
    // show its pilcrow so the change is visible; other empty paragraphs render as nothing
    if (!emptyTrackedBlank(para)) return null;
    const box = paragraphBox(rich, 'diff-paragraph diff-changed ' + (WHOLE_CLASS[para.type] || ''), true);
    const mark = document.createElement('span');
    mark.className = para.type === 'para-insert' ? 'diff-add diff-para-mark' : 'diff-del diff-para-mark';
    mark.title = para.type === 'para-insert' ? 'Blank line inserted' : 'Blank line removed';
    mark.textContent = '\u00B6';
    box.div.appendChild(mark);
    el.appendChild(box.div);
    return box.div;
  }

  if (rich?.isTableRow && rich.cellData) {
    const div = renderTableRow(para, hasChanges);
    el.appendChild(div);
    return div;
  }

  const whole = WHOLE_CLASS[para.type];
  const cls = 'diff-paragraph' + (whole ? ' ' + whole : '') + (hasChanges ? ' diff-changed' : '');
  const box = paragraphBox(rich, cls, !!whole);

  if (para.type === 'modify' && !hasChanges) {
    const text = para.diff!.map((d: DiffEntry) => d.value).join(' ');
    if (!text.trim()) return null;
    fillPlain(box, rich, text);
  } else if (para.type === 'modify' || (para.type === 'move-to' && para.diff)) {
    // Moved-and-edited paragraphs show their in-transit word diff.
    fillDiff(box, para.diff!, sideOf(para.origRich), sideOf(para.revRich), undefined, true);
  } else {
    fillPlain(box, rich, para.text || '');
  }
  if (para.paraMark) {
    // split/merge: words untouched, paragraph break inserted/removed — show the pilcrow
    const mark = document.createElement('span');
    mark.className = para.paraMark === 'inserted' ? 'diff-add diff-para-mark' : 'diff-del diff-para-mark';
    mark.title = para.paraMark === 'inserted' ? 'Paragraph break inserted' : 'Paragraph break removed (merged with the next paragraph)';
    mark.textContent = ' \u00B6';
    box.div.appendChild(mark);
  }
  el.appendChild(box.div);
  return box.div;
}

/** A word diff drawn inline, outside any paragraph (a footnote's or a header's change, in the app's
 *  list of changes outside the body): the same pieces, spacing and classes as a paragraph's. */
export function renderDiffInline(diff: DiffEntry[]): HTMLElement {
  const box = paragraphBox(undefined, 'diff-inline', false);
  fillDiff(box, diff, sideOf(undefined), sideOf(undefined));
  return box.div;
}

// ---------- tables ----------

function sameText(a: string, b: string): boolean {
  return normalizeQuotes((a || '').trim()) === normalizeQuotes((b || '').trim());
}

/** A table row as a one-row table on the document's grid (rows stack into the table). */
function renderTableRow(para: DiffParagraph, hasChanges: boolean): HTMLElement {
  const fromOrig = para.type === 'para-delete' || para.type === 'move-from';
  const row = (fromOrig ? para.origRich : (para.revRich || para.origRich))!;
  const whole = WHOLE_CLASS[para.type] || '';
  const div = document.createElement('div');
  div.className = 'diff-paragraph docx-tr' + (whole ? ' ' + whole : '') + (hasChanges ? ' diff-changed' : '');

  const tbl = resolveTable(row);
  const pos = rowPosition(row);
  const table = document.createElement('table');
  table.className = 'docx-table';
  const total = tbl.gridTw.reduce((a, b) => a + b, 0);
  const tcss: string[] = [];
  // The document's width, or the text column's when that is narrower (a phone): the columns
  // keep their proportions (percentages of the table), so a table shrinks to fit the way
  // paragraphs reflow instead of running off the page. Where it fits, nothing changes.
  const indented = tbl.indTw && tbl.jc !== 'center' && tbl.jc !== 'right' && tbl.jc !== 'end';
  if (total) tcss.push(`width:${tw(total)}`, `max-width:${indented ? `calc(100% - ${tw(tbl.indTw)})` : '100%'}`, 'table-layout:fixed');
  if (tbl.jc === 'center') tcss.push('margin-left:auto', 'margin-right:auto');
  else if (tbl.jc === 'right' || tbl.jc === 'end') tcss.push('margin-left:auto');
  else if (tbl.indTw) tcss.push(`margin-left:${tw(tbl.indTw)}`);
  table.style.cssText = tcss.join(';');
  if (tbl.gridTw.length) {
    const cg = document.createElement('colgroup');
    for (const w of tbl.gridTw) {
      const col = document.createElement('col');
      col.style.width = total ? `${+(w / total * 100).toFixed(3)}%` : tw(w);
      cg.appendChild(col);
    }
    table.appendChild(cg);
  }
  const tr = document.createElement('tr');
  const h = rowHeightTw(row.cellData!.trPrXml);
  if (h) tr.style.height = tw(h);
  table.appendChild(tr);

  const revCells = (fromOrig ? row : (para.revRich || row)).cellData!.cells;
  const origCells = para.origRich?.cellData?.cells || [];
  const diffCells = para.type === 'modify' && hasChanges && rowCellsComparable(para);
  const cells = diffCells ? revCells : row.cellData!.cells;

  cells.forEach((cell, ci) => {
    const td = document.createElement('td');
    const cf = resolveCell(cell.tcPrXml);
    if (cf.span > 1) td.colSpan = cf.span;
    const b = tbl.borders;
    const sides = {
      top: pos.first ? b.top : b.insideH,
      bottom: pos.last ? b.bottom : undefined,
      left: ci === 0 ? b.left : b.insideV,
      right: ci === cells.length - 1 ? b.right : undefined,
    };
    for (const s of ['top', 'bottom', 'left', 'right'] as const) if (cf.borders[s]) sides[s] = cf.borders[s];
    if (cf.vMerge === 'continue') sides.top = undefined;
    const css = [
      `padding:${tw(tbl.cellMar.top)} ${tw(tbl.cellMar.right)} ${tw(tbl.cellMar.bottom)} ${tw(tbl.cellMar.left)}`,
      `vertical-align:${cf.vAlign === 'center' ? 'middle' : cf.vAlign === 'bottom' ? 'bottom' : 'top'}`,
      ...(['top', 'bottom', 'left', 'right'] as const).map(s => `border-${s}:${borderCss(sides[s])}`),
    ];
    if (cf.shd) css.push(`background-color:${cf.shd}`);
    td.style.cssText = css.join(';');

    if (diffCells) {
      const oc = ci < origCells.length ? origCells[ci] : undefined;
      fillDiffCell(td, oc, cell);
      // Cells the revision dropped: their text, struck, at the end of the last cell.
      if (ci === cells.length - 1) for (const extra of origCells.slice(cells.length)) fillCell(td, extra, 'diff-para-del');
    } else {
      fillCell(td, cell, whole);
    }
    tr.appendChild(td);
  });

  div.appendChild(table);
  return div;
}

function fillCell(td: HTMLElement, cell: CellInfo, whole: string): void {
  const paras = cell.paragraphs.length ? cell.paragraphs : [undefined];
  for (const p of paras) {
    const box = paragraphBox(p, 'diff-paragraph cell-p' + (whole ? ' ' + whole : ''), !!whole);
    fillPlain(box, p, '');
    td.appendChild(box.div);
  }
}

/** A changed cell: paragraph by paragraph as the .docx writes it (cellParagraphPlan), else as one. */
function fillDiffCell(td: HTMLElement, oc: CellInfo | undefined, rc: CellInfo): void {
  if (!oc) { fillCell(td, rc, 'diff-para-add'); return; }
  if (sameText(oc.text, rc.text)) { fillCell(td, rc, ''); return; }
  const plan = cellParagraphPlan(oc, rc);
  if (plan) {
    for (const it of plan) {
      if (it.kind === 'delete' || it.kind === 'insert') {
        const p = it.kind === 'delete' ? it.orig : it.rev;
        const box = paragraphBox(p, 'diff-paragraph cell-p ' + (it.kind === 'delete' ? 'diff-para-del' : 'diff-para-add'), true);
        fillPlain(box, p, '');
        td.appendChild(box.div);
        continue;
      }
      const box = paragraphBox(it.rev, 'diff-paragraph cell-p', false);
      if (it.kind === 'equal' || sameText(it.orig.text, it.rev.text)) fillPlain(box, it.rev, '');
      else fillDiff(box, computeWordDiff(it.orig.text, it.rev.text), sideOf(it.orig), sideOf(it.rev));
      td.appendChild(box.div);
    }
    return;
  }
  // Paragraph structure changed: diff the cell's text as one paragraph (as the .docx does).
  const rp = rc.paragraphs[0];
  const op = oc.paragraphs[0];
  const box = paragraphBox(rp, 'diff-paragraph cell-p', false);
  const side = (c: CellInfo, ctx: RichParagraph | undefined): Side => {
    const tokens = c.richTokens || [];
    return { rich: ctx, tokens, gaps: pieceSide(c).gaps };
  };
  fillDiff(box, computeWordDiff(oc.text, rc.text), side(oc, op), side(rc, rp), rp);
  td.appendChild(box.div);
}

export function escapeHtml(text: string | null | undefined): string {
  if (text == null) return '';
  const s = String(text);
  if (!/[&<>"']/.test(s)) return s;
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
